import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { PageHeader, Badge, Btn, Select, Pager, SearchBox, usePaged } from "@/components/erp/ui";
import { toast } from "sonner";
import { MessageCircle, Mail, AlertTriangle, Clock, RefreshCw, Send, CheckCircle2, Hourglass, Factory, ShoppingCart, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ApprovalTasks } from "@/components/erp/ApprovalTasks";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const waNumber = (s) => { let d = String(s || "").replace(/\D/g, ""); if (d.startsWith("0")) d = "62" + d.slice(1); else if (d && !d.startsWith("62")) d = "62" + d; return d; };

const TYPE_META = {
  review: { label: "Perlu Review", icon: Clock, color: "text-sky-600", ring: "border-sky-200 bg-sky-50/60" },
  send: { label: "Perlu Dikirim", icon: Send, color: "text-indigo-600", ring: "border-indigo-200 bg-indigo-50/60" },
  follow_up: { label: "Perlu Follow Up", icon: RefreshCw, color: "text-amber-600", ring: "border-amber-200 bg-amber-50/60" },
  expiry_soon: { label: "Hampir Kedaluwarsa", icon: Hourglass, color: "text-orange-600", ring: "border-orange-200 bg-orange-50/60" },
  reject_reason: { label: "Wajib Pilih Alasan Reject", icon: AlertTriangle, color: "text-rose-600", ring: "border-rose-200 bg-rose-50/60" },
  wo_deadline: { label: "Deadline WO ≤ 2 Hari", icon: Factory, color: "text-red-600", ring: "border-red-300 bg-red-50" },
  pr_approval: { label: "Persetujuan Pembelian", icon: ShoppingCart, color: "text-orange-600", ring: "border-orange-200 bg-orange-50/60" },
  purchase: { label: "Tugas Beli", icon: ShoppingCart, color: "text-teal-700", ring: "border-teal-200 bg-teal-50/60" },
  po_arrival: { label: "PO Belum Datang", icon: Hourglass, color: "text-red-600", ring: "border-red-200 bg-red-50/60" },
  faktur_proof: { label: "Finance: Bukti Faktur Pajak", icon: AlertTriangle, color: "text-violet-700", ring: "border-violet-200 bg-violet-50/60" },
  entertain_review: { label: "Purchasing: Cek Entertainment", icon: ShoppingCart, color: "text-rose-600", ring: "border-rose-200 bg-rose-50/60" },
  entertain_pay: { label: "Finance: Cairkan Entertainment", icon: ShoppingCart, color: "text-emerald-700", ring: "border-emerald-200 bg-emerald-50/60" },
  sales_visit: { label: "Sales: Kunjungan Hari Ini", icon: Send, color: "text-sky-700", ring: "border-sky-200 bg-sky-50/60" },
  sales_chat: { label: "Sales: Chat / Jadwal Meeting", icon: RefreshCw, color: "text-indigo-700", ring: "border-indigo-200 bg-indigo-50/60" },
  day_note: { label: "Catatan Hari Ini", icon: Clock, color: "text-amber-700", ring: "border-yellow-300 bg-yellow-50" },
};

export default function Tasks() {
  const [data, setData] = useState({ tasks: [], rules: null });
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [reasons, setReasons] = useState([]);
  const [reasonSel, setReasonSel] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => { setLoading(true); api.get("/tasks").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.get("/quotations-meta/reject-reasons").then(({ data }) => setReasons(data)).catch(() => {});
  }, []);

  const allRoles = useMemo(() => {
    const s = new Set();
    data.tasks.forEach((t) => (t.roles || []).forEach((r) => s.add(r)));
    return [...s];
  }, [data.tasks]);

  const filtered = useMemo(() => {
    if (roleFilter === "ALL") return data.tasks;
    return data.tasks.filter((t) => (t.roles || []).includes(roleFilter));
  }, [data.tasks, roleFilter]);

  const toReviewing = async (t) => {
    try { await api.put(`/quotations/${t.quotation_id}/status`, { status: "REVIEWING" }); toast.success(`${t.code} → REVIEWING`); load(); }
    catch { toast.error("Gagal mengubah status"); }
  };
  const openWA = async (t, markQuoted) => {
    const txt = `Halo ${t.customer_name}, berikut quotation FOMU 3D PRINT No. ${t.code}. Total: ${formatIDR(t.total || 0)}. PDF: ${BACKEND}/api/public/quotation/${t.quotation_id}/pdf`;
    window.open(`https://wa.me/${waNumber(t.whatsapp)}?text=${encodeURIComponent(txt)}`, "_blank");
    try { await api.post(`/quotations/${t.quotation_id}/mark-sent?channel=wa`); if (markQuoted) { toast.success(`${t.code} ditandai dikirim (→ QUOTED)`); load(); } } catch { /* noop */ }
  };
  const openEmail = async (t, markQuoted) => {
    const subject = `[${t.code}] Quotation FOMU 3D PRINT`;
    const body = `Kepada ${t.customer_name},\n\nBerikut quotation Anda dengan nomor ${t.code}.\nTotal: ${formatIDR(t.total || 0)}.\n\nPDF: ${BACKEND}/api/public/quotation/${t.quotation_id}/pdf\n\nTerima kasih,\nFOMU 3D PRINT`;
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(t.email || "")}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    try { await api.post(`/quotations/${t.quotation_id}/mark-sent?channel=email`); if (markQuoted) { toast.success(`${t.code} ditandai dikirim (→ QUOTED)`); load(); } } catch { /* noop */ }
  };
  const decidePr = async (t, approve) => {
    const note = approve ? "" : window.prompt("Alasan penolakan?");
    if (!approve && !note) return;
    try { await api.post(`/team-pr/${t.pr_id}/decide`, { approve, note }); toast.success(approve ? "Disetujui — masuk ke Purchasing" : "Ditolak"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const saveReason = async (t) => {
    const reason = reasonSel[t.quotation_id];
    if (!reason) { toast.error("Pilih alasan dulu"); return; }
    try { await api.post(`/quotations/${t.quotation_id}/reject-reason`, { reason }); toast.success(`Alasan tersimpan untuk ${t.code}`); load(); }
    catch { toast.error("Gagal menyimpan alasan"); }
  };

  const count = (ty) => data.tasks.filter((t) => t.type === ty).length;
  const [tq, setTq] = useState("");
  const searched = useMemo(() => { const s = tq.trim().toLowerCase(); return s ? filtered.filter((t) => [t.code, t.customer_name, t.status, t.type, t.message, t.whatsapp, t.email].some((v) => String(v || "").toLowerCase().includes(s))) : filtered; }, [filtered, tq]);
  const tp = usePaged(searched, [5, 15]);

  return (
    <div>
      <PageHeader title="Tugas & Reminder" subtitle="Quotation yang lewat SLA dan Work Order yang deadline-nya ≤ 2 hari / lewat. Aturan SLA di Settings → SLA." action={
        <Btn variant="ghost" data-testid="tasks-refresh" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Btn>} />
      <ApprovalTasks />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="tasks-summary">
        {Object.entries(TYPE_META).map(([ty, m]) => (
          <div key={ty} className={`rounded-xl border p-3 ${m.ring}`} data-testid={`tasks-count-${ty}`}>
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><m.icon className={`w-4 h-4 ${m.color}`} /> {m.label}</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{count(ty)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-slate-400">Filter role:</span>
        {["ALL", ...allRoles].map((r) => (
          <button key={r} data-testid={`tasks-role-${r}`} onClick={() => setRoleFilter(r)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${roleFilter === r ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
            {r === "ALL" ? "Semua" : r}
          </button>
        ))}
        <span className="text-xs text-slate-400 font-mono ml-auto">{searched.length} tugas</span>
      </div>
      <div className="mb-3"><SearchBox value={tq} onChange={setTq} testid="tasks-search" placeholder="Cari kode, customer, status…" /></div>

      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center">Memuat tugas…</div>
      ) : searched.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="tasks-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-3" />
          <div className="text-slate-600 font-medium">{tq ? "Tidak ada hasil pencarian" : "Semua quotation terkendali 🎉"}</div>
          <div className="text-slate-400 text-sm mt-1">Tidak ada tugas yang lewat SLA saat ini.</div>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="tasks-list">
          {tp.slice.map((t) => {
            const m = TYPE_META[t.type] || TYPE_META.review;
            return (
              <div key={`${t.quotation_id}-${t.type}`} className={`rounded-xl border p-4 ${m.ring}`} data-testid={`task-${t.quotation_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <m.icon className={`w-4 h-4 ${m.color}`} />
                      <span className="font-mono text-xs text-slate-500">{t.code}</span>
                      <Badge>{t.status}</Badge>
                      <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.age_label}</span>
                    </div>
                    <div className="font-medium text-slate-800 mt-1">{t.customer_name || "-"} {t.type !== "wo_deadline" && t.type !== "pr_approval" && t.type !== "day_note" && <span className="text-slate-400 font-normal">· {formatIDR(t.total || 0)}</span>}{t.express && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold">EXPRESS</span>}</div>
                    <div className={`text-sm mt-0.5 ${m.color}`}>{t.label}</div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      PIC: <span className="font-medium text-slate-600">{t.pic || "—"}</span>
                      {(t.roles || []).length > 0 && <span> · Role: {(t.roles || []).join(", ")}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {(t.type === "entertain_review" || t.type === "entertain_pay") && (
                      <Btn data-testid={`task-open-ent-${t.quotation_id}`} onClick={() => navigate(t.type === "entertain_review" ? "/app/purchasing" : "/app/finance")}><ShoppingCart className="w-4 h-4" /> {t.type === "entertain_review" ? "Cek di Purchasing" : "Cairkan di Finance"}</Btn>
                    )}
                    {(t.type === "sales_visit" || t.type === "sales_chat") && (
                      <Btn data-testid={`task-open-sales-${t.quotation_id}`} onClick={() => navigate("/app/sales")}><Send className="w-4 h-4" /> Buka Dashboard Sales</Btn>
                    )}
                    {(t.type === "purchase" || t.type === "po_arrival") && (
                      <Btn data-testid={`task-open-purchasing-${t.quotation_id}`} onClick={() => navigate("/app/purchasing")}><ShoppingCart className="w-4 h-4" /> Buka Purchasing</Btn>
                    )}
                    {t.type === "pr_approval" && (<>
                      <Btn data-testid={`task-pr-approve-${t.pr_id}`} onClick={() => decidePr(t, true)}><Check className="w-4 h-4" /> Setujui</Btn>
                      <Btn variant="ghost" data-testid={`task-pr-reject-${t.pr_id}`} onClick={() => decidePr(t, false)}>Tolak</Btn>
                    </>)}
                    {t.type === "faktur_proof" && (
                      <Btn data-testid={`task-open-invoice-${t.invoice_id}`} onClick={() => navigate(`/app/invoices?open=${t.invoice_id}`)}>Upload Faktur Pajak</Btn>
                    )}
                    {t.type === "day_note" && (
                      <Btn data-testid={`task-note-done-${t.note_id}`} onClick={() => api.put(`/day-notes/${t.note_id}/done`).then(() => { toast.success("Catatan selesai"); load(); }).catch(() => toast.error("Gagal"))}><Check className="w-4 h-4" /> Selesai</Btn>
                    )}
                    {t.type === "wo_deadline" && (
                      <Btn data-testid={`task-open-wo-${t.wo_id}`} onClick={() => navigate(`/app/workorders?open=${t.wo_id}`)}><Factory className="w-4 h-4" /> Buka WO</Btn>
                    )}
                    {t.type === "review" && (
                      <Btn data-testid={`task-review-${t.quotation_id}`} onClick={() => toReviewing(t)}><Clock className="w-4 h-4" /> Ubah ke REVIEWING</Btn>
                    )}
                    {t.type === "send" && (<>
                      <Btn variant="ghost" data-testid={`task-wa-${t.quotation_id}`} onClick={() => openWA(t, true)}><MessageCircle className="w-4 h-4" /> WA</Btn>
                      <Btn variant="ghost" data-testid={`task-email-${t.quotation_id}`} onClick={() => openEmail(t, true)}><Mail className="w-4 h-4" /> Email</Btn>
                    </>)}
                    {(t.type === "follow_up" || t.type === "expiry_soon") && (<>
                      <Btn variant="ghost" data-testid={`task-wa-${t.quotation_id}`} onClick={() => openWA(t, false)}><MessageCircle className="w-4 h-4" /> WA</Btn>
                      <Btn variant="ghost" data-testid={`task-email-${t.quotation_id}`} onClick={() => openEmail(t, false)}><Mail className="w-4 h-4" /> Email</Btn>
                    </>)}
                    {t.type === "reject_reason" && (
                      <div className="flex items-center gap-2">
                        <Select data-testid={`task-reason-${t.quotation_id}`} value={reasonSel[t.quotation_id] || ""} onChange={(e) => setReasonSel((s) => ({ ...s, [t.quotation_id]: e.target.value }))}>
                          <option value="">— Pilih alasan —</option>
                          {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                        <Btn data-testid={`task-reason-save-${t.quotation_id}`} onClick={() => saveReason(t)}>Simpan</Btn>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="rounded-xl border border-slate-200 bg-white"><Pager p={tp} testid="tasks-pager" unit="notifikasi" /></div>
        </div>
      )}
    </div>
  );
}
